import { Observable } from 'rxjs';
import httpService from './httpService';
import { PricingFunction } from '../data/models/pricing-function.model';
import {
  AccountingState,
  PricingModel,
} from '../data/models/pricing-model.model';
import {
  CreatableProviderServicePricing,
  EditableProviderServicePricing,
  ProviderServicePricing,
} from '../data/models/provider-service-pricing.model';
import { TransportationService } from '../data/models/transportation-service.model';
import { TransportationAttribute } from '../data/models/transportation-attribute.model';
import { TripLegPricings } from '../data/models/trip-leg-pricings.model';

class AccountingService {
  private readonly apiBase;
  private readonly apiBasePublic;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_ACCOUNTING_API_URL.trim();
    this.apiBasePublic =
      __environment__.REACT_APP_PUBLIC_ACCOUNTING_API_URL.trim();
  }

  // ========================================
  // BEGIN PRICING FUNCTIONS
  // ========================================

  public getPricingFunctions(
    states?: AccountingState[],
  ): Observable<PricingFunction[]> {
    let url = `${this.apiBase}/getPricingFunctions`;
    if (states?.length) {
      url += `?states=${states.join(',')}`;
    }
    return httpService.getJSON(url);
  }
  // ========================================
  // END PRICING FUNCTIONS
  // ========================================

  // ========================================
  // BEGIN PRICNG MODEL FUNCTIONS
  // ========================================
  public getPricingModels(
    states?: AccountingState[],
  ): Observable<PricingModel[]> {
    let url = `${this.apiBase}/getPricingModels`;
    if (states?.length) {
      url += `?states=${states.join(',')}`;
    }
    return httpService.getJSON(url);
  }

  public createPricingModel(
    pricingModel: PricingModel,
  ): Observable<PricingModel> {
    return httpService.postJSON(
      `${this.apiBase}/createPricingModel`,
      pricingModel,
    );
  }

  public updatePricingModel(
    pricingModel: PricingModel,
  ): Observable<PricingModel> {
    return httpService.postJSON(
      `${this.apiBase}/updatePricingModel`,
      pricingModel,
    );
  }

  public deletePricingModel(pricingModelUUID: string): Observable<void> {
    const url = `${this.apiBase}/deletePricingModel`;
    return httpService.postJSON(url, {
      pricingModelUUID,
    });
  }

  public updatePricingModelEffectiveDates(
    pricingModelUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<PricingModel> {
    const url = `${this.apiBase}/updatePricingModelEffectiveDates`;
    return httpService.postJSON(url, {
      pricingModelUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }
  // ========================================
  // END PRICING MODEL FUNCTIONS
  // ========================================

  // ========================================
  // BEGIN PROVIDER SERVICE PRICNG FUNCTIONS
  // ========================================
  public getProviderServicePricings(
    transportationProviderID?: number,
    states?: AccountingState[],
  ): Observable<ProviderServicePricing[]> {
    let url = `${this.apiBase}/getProviderServicePricings`;
    if (transportationProviderID) {
      url += `?transportationProviderID=${transportationProviderID}`;
    }
    if (transportationProviderID && states?.length) {
      url += `&states=${states.join(',')}`;
    } else if (states?.length) {
      url += `?states=${states.join(',')}`;
    }
    return httpService.getJSON(url);
  }

  public createProviderServicePricing(
    psp: CreatableProviderServicePricing,
  ): Observable<ProviderServicePricing> {
    const url = `${this.apiBase}/createProviderServicePricing`;
    return httpService.postJSON(url, psp);
  }

  public updateProviderServicePricing(
    psp: EditableProviderServicePricing,
  ): Observable<ProviderServicePricing> {
    const url = `${this.apiBase}/updateProviderServicePricing`;
    return httpService.postJSON(url, psp);
  }

  public deleteProviderServicePricing(id: string): Observable<void> {
    const url = `${this.apiBase}/deleteProviderServicePricing`;
    return httpService.postJSON(url, { providerServicePricingUUID: id });
  }

  public updateProviderServicePricingEffectiveDates(
    providerServicePricingUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<ProviderServicePricing> {
    const url = `${this.apiBase}/updateProviderServicePricingEffectiveDates`;
    return httpService.postJSON(url, {
      providerServicePricingUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }
  // ========================================
  // END PROVIDER SERVICE PRICING FUNCTIONS
  // ========================================

  // ========================================
  // BEGIN TRANSPORTATION SERVICE FUNCTIONS
  // ========================================
  public getTransportationServices(
    states?: string[],
  ): Observable<TransportationService[]> {
    let url = `${this.apiBase}/getTransportationServices`;
    if (states?.length) {
      url += `&states=${states.join(',')}`;
    }
    return httpService.getJSON(url);
  }
  // ========================================
  // END TRANSPORTATION SERVICE FUNCTIONS
  // ========================================

  // ========================================
  // BEGIN TRANSPORTATION ATTRIBUTE FUNCTIONS
  // ========================================
  public getTransportationAttributes(
    states?: string[],
  ): Observable<TransportationAttribute[]> {
    let url = `${this.apiBasePublic}/transportation-attributes`;
    if (states?.length) {
      url += `&states=${states.join(',')}`;
    }
    return httpService.getJSON(url);
  }
  // ========================================
  // END TRANSPORTATION SERVICE FUNCTIONS
  // ========================================

  public getTripLegPricings(
    passengerUUID: string,
    tripLegReferenceID: string,
    filter?: 'client' | 'provider',
    tripLegState?: 'active' | 'inactive',
  ): Observable<TripLegPricings> {
    let url = `${this.apiBase}/getTripLegPricings?passengerUUID=${passengerUUID}&tripLegReferenceID=${tripLegReferenceID}`;
    if (filter) {
      url += `&filter=${filter}`;
    }
    if (tripLegState) {
      url += `&tripLegState=${tripLegState}`;
    }
    return httpService.getJSON(url);
  }
}

const accountingService = new AccountingService();

export default accountingService;
